import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { ListItemIcon, ListItemText, Stack, Typography } from "@mui/material";
import { ReactRenderer } from "@tiptap/react";
import { PluginKey } from "@tiptap/pm/state";
import tippy from "tippy.js";

import { HubIcon } from "../../HubIcon";
import { StyledMenu, StyledMenuItem } from "../../HubContextMenu/styles";
import api from "../../../utils/api";

import { ENTITY_CONFIG } from "./EntityLinkComponent";

const EntityList = forwardRef(({ items, command }, ref) => {
  const [selected, setSelected] = useState(0);

  useEffect(() => setSelected(0), [items]);

  const selectItem = (index) => {
    const item = items[index];
    if (item) command(item);
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (event.key === "ArrowUp") {
        setSelected((s) => (s + items.length - 1) % items.length);
        return true;
      }
      if (event.key === "ArrowDown") {
        setSelected((s) => (s + 1) % items.length);
        return true;
      }
      if (event.key === "Enter") {
        selectItem(selected);
        return true;
      }
      return false;
    },
  }));

  const grouped = items.reduce((acc, item) => {
    if (!acc[item.entityType]) acc[item.entityType] = [];
    acc[item.entityType].push(item);
    return acc;
  }, {});

  return (
    <Stack
      sx={(theme) => ({
        border: "1px solid",
        borderColor: theme.palette.hubColors.greyLight,
        borderRadius: "4px",
        boxShadow: "0px 2px 7px rgba(0,0,0,0.25)",
        backgroundColor: theme.palette.hubColors.white,
        maxHeight: 300,
        overflow: "auto",
        minWidth: 240,
        padding: "10px 5px",
      })}
    >
      {!items.length && (
        <Typography
          variant="body2"
          sx={{ fontSize: 13, color: "text.disabled", padding: "10px 15px" }}
        >
          No results found
        </Typography>
      )}
      {Object.entries(grouped).map(([type, groupItems]) => {
        const config = ENTITY_CONFIG[type] || {};
        return (
          <Stack key={type}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "hubColors.grey",
                padding: "8px 15px 2px",
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {config.label || type}
            </Typography>
            {groupItems.map((item) => {
              const idx = items.indexOf(item);
              return (
                <StyledMenuItem
                  key={`${item.entityType}-${item.entityId}`}
                  selected={idx === selected}
                  onClick={() => selectItem(idx)}
                >
                  <Stack
                    direction="row"
                    gap="15px"
                    alignItems="center"
                    width="100%"
                  >
                    <ListItemIcon sx={{ minWidth: "auto !important" }}>
                      <HubIcon icon={config.icon || "Link"} />
                    </ListItemIcon>
                    <ListItemText>
                      <Stack gap="2px">
                        <Typography
                          variant="boldCaptionMainL"
                          lineHeight="12px"
                        >
                          {item.label}
                        </Typography>
                        {item.subtitle && (
                          <Typography variant="caption" lineHeight="12px">
                            {item.subtitle}
                          </Typography>
                        )}
                      </Stack>
                    </ListItemText>
                  </Stack>
                </StyledMenuItem>
              );
            })}
          </Stack>
        );
      })}
    </Stack>
  );
});

export const entityLinkPluginKey = new PluginKey("entityLink");

export const getEntitySuggestion = () => ({
  char: "[[",
  pluginKey: entityLinkPluginKey,
  allowedPrefixes: null,
  allowSpaces: false,
  command: ({ editor, range, props }) => {
    editor
      .chain()
      .focus()
      .deleteRange(range)
      .insertContent([
        {
          type: "entityLink",
          attrs: {
            entityType: props.entityType,
            entityId: props.entityId,
            label: props.label,
            prefix: props.prefix,
            route: props.route,
          },
        },
        { type: "text", text: " " },
      ])
      .run();
  },
  items: async ({ query }) => {
    if (!query || query.length < 1) return [];
    try {
      const results = await api.get(
        `/search/entities?q=${encodeURIComponent(query)}`,
      );
      return results || [];
    } catch {
      return [];
    }
  },
  render: () => {
    let component;
    let popup;

    return {
      onStart: (props) => {
        component = new ReactRenderer(EntityList, {
          props,
          editor: props.editor,
        });
        popup = tippy("body", {
          getReferenceClientRect: props.clientRect,
          appendTo: () => document.body,
          content: component.element,
          showOnCreate: true,
          interactive: true,
          trigger: "manual",
          placement: "bottom-start",
        });
      },
      onUpdate: (props) => {
        component?.updateProps(props);
        popup?.[0]?.setProps({ getReferenceClientRect: props.clientRect });
      },
      onKeyDown: (props) => component?.ref?.onKeyDown(props),
      onExit: () => {
        popup?.[0]?.destroy();
        component?.destroy();
      },
    };
  },
});
